// TypeIcon.jsx

import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Firebase
import { db } from 'firebaseConfig';

// CSS
import './TypeIcon.css';

// Components
import IconPickerButton from 'components/iconpicker/IconPickerButton';

// Services
import { updateNodeTypeIconKey } from 'services/nodetypes/NodeTypeMutationService';

// Cache
import {
    getNodeTypesCacheForScope,
    makeWorkspaceScopeKeyStrict,
    warmNodeTypesCache,
} from 'services/nodetypes/NodeTypesCacheService';

/**
 * TypeIcon
 *
 * Displays a node type icon and allows updating the iconKey via IconPicker.
 *
 * @param {string} typeKey - Node type key.
 * @param {number} size - Icon button size in pixels.
 * @param {string} iconKey - Optional controlled icon key.
 * @param {function} onChange - Optional controlled icon change handler.
 * @param {boolean} disabled - Optional disabled override.
 *
 * @returns {ReactNode} - TypeIcon.
 */
const TypeIcon = React.memo(({ typeKey, size = 46, iconKey, onChange, disabled = false }) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Node Types Context
    const { getType } = useNodeTypes();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // ----- STATE ----- //

    const [saving, setSaving] = useState(false);

    // ----- MEMOS ----- //

    const typeDef = typeKey ? getType(typeKey) : null;

    // We derive the active workspace key here since we need it for the workspace cache scope key and 
    // we want to ensure we have a stable value for the workspace cache.
    const activeWorkspaceKey = typeof activeWorkspace?.key === 'string'
        ? activeWorkspace.key
        : '';

    const controlledIconKey = typeof iconKey === 'string' ? iconKey.trim() : '';
    const isControlled = typeof onChange === 'function';

    // We derive a scope key for the workspace cache based on the active workspace key. This allows 
    // us to have a separate cache for the workspace scope, which can be more up-to-date for the workspace 
    // type definition and can receive updates when the workspace type definition changes.
    const workspaceScopeKey = activeWorkspaceKey
        ? makeWorkspaceScopeKeyStrict(activeWorkspaceKey)
        : null;

    // We want to have a separate cache for the workspace scope since it will be more up-to-date 
    // for the workspace type definition, which allows us to show the latest icon and to have the most 
    // recent data when the user opens the IconPicker. This is especially important if the user has made 
    // recent changes to the type or if there are multiple users collaborating in the workspace, which could 
    // lead to frequent updates to the type definition.
    const workspaceCache = useMemo(
        () => (workspaceScopeKey ? getNodeTypesCacheForScope(workspaceScopeKey) : null),
        [workspaceScopeKey]
    );

    // ----- EFFECTS ----- //

    // Use useSyncExternalStore to subscribe to changes in the workspace cache's revision, which 
    // allows us to reactively update the icon if the workspace type definition changes due to external 
    // factors (e.g. another user updating the type).
    useSyncExternalStore(
        (cb) => (workspaceCache ? workspaceCache.onChange(cb) : () => {}),
        () => (workspaceCache ? workspaceCache.rev : ''),
        () => (workspaceCache ? workspaceCache.rev : '')
    );

    // Ensure the workspace cache is warmed for the active workspace to have the most 
    // up-to-date data and to enable instant loading when the user opens the IconPicker.
    const workspaceTypeDef = !workspaceCache || !typeKey
        ? null
        : (workspaceCache.get(typeKey) || null);

    // Fall back to the workspace type definition if it exists since it will be more 
    // up-to-date than the global type definition, which may be stale if the workspace cache has 
    // been warmed and has received updates.
    const resolvedIconKey = useMemo(() => {
        if (isControlled) {
            return controlledIconKey || 'deployed_code';
        }

        const workspaceKeyValue = typeof workspaceTypeDef?.iconKey === 'string'
            ? workspaceTypeDef.iconKey.trim()
            : '';
        if (workspaceKeyValue) return workspaceKeyValue;

        const mergedKeyValue = typeof typeDef?.iconKey === 'string' ? typeDef.iconKey.trim() : '';
        return mergedKeyValue || 'deployed_code';
    }, [controlledIconKey, isControlled, typeDef?.iconKey, workspaceTypeDef?.iconKey]);
    
    // Warm the workspace cache on mount if we have an active workspace to ensure we have the latest data 
    // and to enable instant loading in the IconPicker.
    useEffect(() => {
        if (!workspaceScopeKey) return undefined;
        warmNodeTypesCache(db, { scopeKey: workspaceScopeKey, subscribe: true }).catch((error) => {
            console.error('[TypeIcon] Failed to warm workspace cache:', error);
        });
        return undefined;
    }, [workspaceScopeKey]);

    const isWorkspaceType = isControlled || Boolean(workspaceTypeDef?.key);
    const isDisabled = disabled || saving || (!isControlled && (!typeKey || !activeWorkspaceKey || !isWorkspaceType));

    // ----- HANDLERS ----- //

    /**
     * Handles icon selection from the IconPicker.
     * 
     * @param {string} selectedKey - The key of the selected icon.
     * @param {object} options - Additional options.
     * @param {function} options.closePicker - Function to close the IconPicker popup.
     */
    const handleSelect = useCallback(async (selectedKey, { closePicker } = {}) => {
        if (saving) return;

        const nextKey = typeof selectedKey === 'string' ? selectedKey.trim() : '';

        if (isControlled) {
            onChange(nextKey);
            closePicker?.();
            return;
        }

        if (!typeKey || !activeWorkspaceKey) return;

        setSaving(true);

        try {
            await updateNodeTypeIconKey({
                workspaceKey: activeWorkspaceKey,
                typeKey,
                iconKey: nextKey,
            });
            closePicker?.();
        } catch (error) {
            console.error('Failed to update icon key:', error);
            alert(error?.message || 'Failed to update icon key.');
        } finally {
            setSaving(false);
        }
    }, [activeWorkspaceKey, isControlled, onChange, saving, typeKey]);

    // ----- RENDER ----- //

    return (

        <IconPickerButton
            className="type-icon-container"
            backgroundColor="transparent"
            borderRadius={9}
            buttonSize={size}
            disabled={isDisabled}
            disabledTitle=""
            hoverBackgroundColor={theme.backgroundMuted}
            iconColor={theme.foreground}
            iconSize={30}
            popupHeight={400}
            popupWidth={400}
            saving={saving}
            selectedIconKey={resolvedIconKey}
            title="Choose icon"
            onSelect={handleSelect}
        />

    );
});

// Dev Tools
TypeIcon.displayName = 'TypeIcon';

export default TypeIcon;
